export const CHECKOUT_TRACKING_KEYS = {
  POPUP_ID: "_weupsell_popupId",
  PIXEL: "_weupsell_pixel",
  REWARD_TIER_ID: "_weupsell_rewardTierId",
  /** Matched offer rule id (`default` or rule id) for per-source product discounts. */
  OFFER_RULE_ID: "_weupsell_offerRuleId",
  /** Persists after discount revoke; marks cart-drawer upsell adds only. */
  CART_DRAWER_UPSELL_LINE: "_weupsell_cartDrawerUpsell",
};

/** Runtime stamp on hydrated offer products (not a cart line property key). */
export const OFFER_PRODUCT_RULE_ID_FIELD = "__weupsellOfferRuleId";

export function resolveOfferRuleIdFromProduct(product) {
  if (!product || typeof product !== "object") return "";
  const value = product[OFFER_PRODUCT_RULE_ID_FIELD] ?? product.offerRuleId;
  return value != null && String(value) !== "" ? String(value) : "";
}

export function resolveOfferRuleIdForTracking({
  offerRuleId,
  product,
  popup,
} = {}) {
  if (offerRuleId != null && String(offerRuleId) !== "") {
    return String(offerRuleId);
  }
  const fromProduct = resolveOfferRuleIdFromProduct(product);
  if (fromProduct) return fromProduct;
  if (
    popup?.__weupsellMatchedOfferRuleId != null &&
    String(popup.__weupsellMatchedOfferRuleId) !== ""
  ) {
    return String(popup.__weupsellMatchedOfferRuleId);
  }
  return "default";
}

export const CHECKOUT_PIXEL_VERSION = 1;

/** Storefront popup upsell surface (Ajax /cart/add.js line item properties). */
export const STOREFRONT_TRACKING_LOCATION = "STOREFRONT";

/** Product-link PDP attribution (theme ATC after upsell image/title click). */
export const PRODUCT_LINK_LOCATION = "PRODUCT_LINK";

/**
 * Shared `referalby=weupsell-ai` PDP visit (friend/share).
 * App-level only — excluded from campaign/template revenue.
 */
export const APP_REFERRAL_LOCATION = "APP_REFERRAL";

/** Synthetic popup id for APP_REFERRAL line properties / conversion dedup. */
export const APP_REFERRAL_POPUP_ID = "weupsell-app-referral";

/**
 * Sentinel campaign ObjectId for APP_REFERRAL conversion_dedup rows
 * (not a real campaign; never incremented into CampaignAnalytics).
 */
export const APP_REFERRAL_CAMPAIGN_SENTINEL = "000000000000000000000000";

/** Post-purchase extension upsell surface (applyChangeset order edit). */
export const POST_PURCHASE_LOCATION = "POST_PURCHASE";

/** Shopify Admin order tag applied when WeUpsell attributed the order (like ReConvert). */
export const WEUPSELL_ORDER_TAG = "weupsell";

/**
 * Round monetary line revenue to 2 decimal places (shop currency cents).
 * @param {number | string | null | undefined} amount
 * @returns {number}
 */
export function normalizeLineRevenue(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value * 100) / 100);
}

/**
 * @param {string | number | undefined | null} id
 * @returns {string}
 */
export function extractShopifyNumericId(id) {
  if (id == null || id === "") return "";
  const str = String(id);
  const match = str.match(/(\d+)\s*$/);
  return match ? match[1] : str;
}

/**
 * @param {string | undefined | null} extensionTarget
 * @returns {"CHECKOUT" | "THANK_YOU" | "ORDER_STATUS"}
 */
export function getCheckoutLocation(extensionTarget) {
  const target = String(extensionTarget || "");
  if (target.includes("thank-you")) return "THANK_YOU";
  if (target.includes("order-status")) return "ORDER_STATUS";
  return "CHECKOUT";
}

/**
 * @param {string | undefined | null} value
 * @returns {Record<string, unknown> | null}
 */
export function parseCheckoutPixelProperty(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * @param {Array<{ key?: string; value?: string }> | undefined} attributes
 * @param {string} key
 * @returns {string | undefined}
 */
export function getLineAttributeValue(attributes, key) {
  if (!Array.isArray(attributes)) return undefined;
  return attributes.find((attr) => attr?.key === key)?.value;
}

/**
 * Normalize Mongo / API ids to a cart-safe string.
 * Avoids `String({...})` → `"[object Object]"` which breaks discount matching.
 * @param {unknown} value
 * @returns {string}
 */
export function toTrackingIdString(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number") {
    const s = String(value).trim();
    return s === "[object Object]" ? "" : s;
  }
  if (typeof value !== "object") return "";

  // BSON / Mongoose ObjectId
  if (typeof value.toHexString === "function") {
    try {
      const hex = value.toHexString();
      if (typeof hex === "string" && hex && hex !== "[object Object]") {
        return hex;
      }
    } catch {
      /* ignore */
    }
  }
  if (typeof value.toString === "function") {
    const viaToString = value.toString();
    if (
      typeof viaToString === "string" &&
      viaToString &&
      viaToString !== "[object Object]"
    ) {
      return viaToString;
    }
  }

  // Extended JSON / nested / populated docs
  if (value.$oid != null) return toTrackingIdString(value.$oid);
  if (value._id != null && value._id !== value) {
    return toTrackingIdString(value._id);
  }
  if (value.id != null && value.id !== value) {
    return toTrackingIdString(value.id);
  }

  return "";
}

/**
 * Resolve popup id from runtime/editor payloads (_id, id, popupId).
 * @param {{ _id?: string | number; id?: string | number; popupId?: string | number } | null | undefined} popup
 * @returns {string}
 */
export function resolveTrackingPopupId(popup) {
  if (!popup || typeof popup !== "object") return "";
  return (
    toTrackingIdString(popup._id) ||
    toTrackingIdString(popup.id) ||
    toTrackingIdString(popup.popupId) ||
    ""
  );
}

/**
 * @param {{
 *   shop?: { myshopifyDomain?: string } | string;
 *   extensionTarget?: string;
 *   popup?: { _id?: string; campaign_Id?: string; popUpType?: string };
 *   product?: { id?: string | number; admin_graphql_api_id?: string };
 *   variant?: { id?: string | number; admin_graphql_api_id?: string };
 *   quantity?: number;
 *   slotNo?: string;
 *   location?: string;
 *   offerRuleId?: string | null;
 * }} params
 * @returns {Array<{ key: string; value: string }>}
 */
export function buildCheckoutCartAttributes({
  shop,
  extensionTarget,
  popup,
  product,
  variant,
  quantity = 1,
  slotNo,
  location,
  offerRuleId,
}) {
  const popupId = resolveTrackingPopupId(popup);
  if (!popupId) return [];

  const store =
    typeof shop === "string" ? shop : shop?.myshopifyDomain || "";

  const productId = extractShopifyNumericId(
    product?.id ?? product?.admin_graphql_api_id
  );
  const variantId = extractShopifyNumericId(
    variant?.id ?? variant?.admin_graphql_api_id
  );

  const resolvedOfferRuleId = resolveOfferRuleIdForTracking({
    offerRuleId,
    product,
    popup,
  });

  const pixel = {
    v: CHECKOUT_PIXEL_VERSION,
    store,
    location: location || getCheckoutLocation(extensionTarget),
    popupId,
    campaignId: toTrackingIdString(popup?.campaign_Id),
    productId,
    variantId,
    quantity: Number(quantity) || 1,
    slot: slotNo || "",
    popupType: popup?.popUpType || "",
    offerRuleId: resolvedOfferRuleId,
  };

  return [
    { key: CHECKOUT_TRACKING_KEYS.POPUP_ID, value: popupId },
    { key: CHECKOUT_TRACKING_KEYS.PIXEL, value: JSON.stringify(pixel) },
    { key: CHECKOUT_TRACKING_KEYS.OFFER_RULE_ID, value: resolvedOfferRuleId },
  ];
}

/**
 * Build a Storefront cartCreate line item with WeUpsell tracking attributes.
 *
 * @param {{
 *   shop?: { myshopifyDomain?: string } | string;
 *   extensionTarget?: string;
 *   popup?: { _id?: string; campaign_Id?: string; popUpType?: string };
 *   product?: { id?: string | number; admin_graphql_api_id?: string };
 *   variant?: { id?: string | number; admin_graphql_api_id?: string };
 *   quantity?: number;
 *   slotNo?: string;
 * }} params
 * @returns {{ id: string; quantity: number; attributes?: Array<{ key: string; value: string }> } | null}
 */
export function buildCheckoutCartLineItem({
  shop,
  extensionTarget,
  popup,
  product,
  variant,
  quantity = 1,
  slotNo,
}) {
  const variantId = extractShopifyNumericId(
    variant?.id ?? variant?.admin_graphql_api_id
  );
  if (!variantId) return null;

  const attributes = buildCheckoutCartAttributes({
    shop,
    extensionTarget,
    popup,
    product,
    variant,
    quantity,
    slotNo,
  });

  return {
    id: variantId,
    quantity: Number(quantity) || 1,
    ...(attributes.length ? { attributes } : {}),
  };
}

/**
 * Convert checkout cart attributes to Shopify Ajax API line item properties.
 *
 * @param {Array<{ key?: string; value?: string }> | undefined} attributes
 * @returns {Record<string, string> | undefined}
 */
export function cartAttributesToLineProperties(attributes) {
  if (!Array.isArray(attributes) || attributes.length === 0) return undefined;

  const properties = {};
  for (const attribute of attributes) {
    const key = attribute?.key;
    if (!key || attribute?.value == null) continue;
    properties[key] = String(attribute.value);
  }

  return Object.keys(properties).length ? properties : undefined;
}

/**
 * Convert Ajax cart line properties (or Storefront attributes) to CartLineInput attributes.
 *
 * @param {Record<string, string> | Array<{ name?: string; key?: string; value?: string }> | null | undefined} properties
 * @returns {Array<{ key: string; value: string }>}
 */
export function linePropertiesToCartAttributes(properties) {
  if (!properties) return [];

  if (Array.isArray(properties)) {
    return properties
      .map((prop) => ({
        key: String(prop?.key || prop?.name || "").trim(),
        value: prop?.value != null ? String(prop.value) : "",
      }))
      .filter((attr) => attr.key && attr.value);
  }

  if (typeof properties === "object") {
    return Object.entries(properties)
      .map(([key, value]) => ({
        key: String(key),
        value: value != null ? String(value) : "",
      }))
      .filter((attr) => attr.key && attr.value);
  }

  return [];
}

/**
 * Normalize checkout API line items — preserve WeUpsell tracking from Ajax cart properties.
 *
 * @param {{ id?: string | number; quantity?: number; selling_plan?: string | number; properties?: Record<string, string>; attributes?: Array<{ key?: string; value?: string }> } | null | undefined} item
 * @returns {{ id?: string | number; quantity?: number; selling_plan?: string | number; properties?: Record<string, string>; attributes?: Array<{ key: string; value: string }> }}
 */
export function normalizeCheckoutApiLineItem(item) {
  if (!item || typeof item !== "object") return item;

  const fromAttributes = linePropertiesToCartAttributes(item.attributes);
  const attributes = fromAttributes.length
    ? fromAttributes
    : linePropertiesToCartAttributes(item.properties);

  if (!attributes.length) return item;

  return {
    ...item,
    attributes,
  };
}

/**
 * Resolve selling plan id from a Shopify Ajax /cart.js line item.
 *
 * @param {{ selling_plan_allocation?: { selling_plan?: { id?: number }; selling_plan_id?: number } } | null | undefined} cartLine
 * @returns {number | null}
 */
export function resolveSellingPlanIdFromCartJsLine(cartLine) {
  if (!cartLine || typeof cartLine !== "object") return null;
  const allocation = cartLine.selling_plan_allocation;
  if (!allocation || typeof allocation !== "object") return null;

  const raw =
    allocation.selling_plan?.id ??
    allocation.selling_plan_id ??
    null;
  if (raw == null) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Map Shopify Ajax /cart.js line to the public checkout API payload.
 *
 * @param {{ variant_id?: number; quantity?: number; properties?: Record<string, string>; selling_plan_allocation?: { selling_plan?: { id?: number }; selling_plan_id?: number } } | null | undefined} cartLine
 */
export function mapStorefrontCartJsLineToCheckoutItem(cartLine) {
  if (!cartLine) return null;

  const sellingPlanId = resolveSellingPlanIdFromCartJsLine(cartLine);

  return normalizeCheckoutApiLineItem({
    id: cartLine.variant_id,
    quantity: cartLine.quantity ?? 1,
    ...(cartLine.properties ? { properties: cartLine.properties } : {}),
    ...(sellingPlanId != null ? { selling_plan: sellingPlanId } : {}),
  });
}

/**
 * Build Ajax /cart/add.js line item properties for storefront popup upsells.
 *
 * @param {{
 *   shop?: { myshopifyDomain?: string } | string;
 *   popup?: { _id?: string; campaign_Id?: string; popUpType?: string };
 *   product?: { id?: string | number; admin_graphql_api_id?: string };
 *   variant?: { id?: string | number; admin_graphql_api_id?: string };
 *   quantity?: number;
 *   slotNo?: string;
 *   cartDrawerUpsell?: boolean;
 * }} params
 * @returns {Record<string, string> | undefined}
 */
export function buildStorefrontCartLineProperties(params) {
  const attributes = buildCheckoutCartAttributes({
    ...params,
    location: STOREFRONT_TRACKING_LOCATION,
  });

  const properties = cartAttributesToLineProperties(attributes) || {};
  if (params?.cartDrawerUpsell) {
    const popupId = resolveTrackingPopupId(params.popup);
    if (popupId) {
      properties[CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE] = popupId;
    }
  }

  return Object.keys(properties).length ? properties : undefined;
}

/**
 * @param {{ properties?: Record<string, string> } | null | undefined} item
 * @returns {boolean}
 */
export function lineItemHasStorefrontTracking(item) {
  return Boolean(item?.properties?.[CHECKOUT_TRACKING_KEYS.POPUP_ID]);
}

export function lineItemHasRewardTierTracking(item) {
  return Boolean(item?.properties?.[CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID]);
}

/**
 * Attach `_weupsell_popupId` / `_weupsell_pixel` / `_weupsell_offerRuleId` to Ajax cart lines.
 * Always refreshes offer-rule stamp when ctx provides a resolved rule (same product can
 * have different discounts on Default vs Offer Rules).
 *
 * @param {Array<{ id?: string | number; quantity?: number; properties?: Record<string, string> }>} items
 * @param {{
 *   shop?: string;
 *   popup?: object;
 *   products?: object[];
 *   product?: object;
 *   productIndex?: number;
 *   slotNo?: string;
 *   offerRuleId?: string;
 *   cartDrawerUpsell?: boolean;
 *   getProductForIndex?: (index: number) => object | null | undefined;
 * }} ctx
 * @returns {typeof items}
 */
export function attachStorefrontTrackingToCartItems(items, ctx = {}) {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (!resolveTrackingPopupId(ctx.popup)) return items;

  const shop =
    ctx.shop ||
    (typeof window !== "undefined"
      ? window?.Shopify?.shop || window.location.hostname
      : "");

  return items.map((item, index) => {
    if (lineItemHasRewardTierTracking(item)) return item;

    const slotNo =
      ctx.slotNo != null ? String(ctx.slotNo) : String(ctx.productIndex ?? index);

    const product =
      typeof ctx.getProductForIndex === "function"
        ? ctx.getProductForIndex(index)
        : ctx.products?.[index] ??
          (index === (ctx.productIndex ?? 0)
            ? ctx.product ?? ctx.products?.[ctx.productIndex ?? 0]
            : ctx.product);

    const resolvedOfferRuleId = resolveOfferRuleIdForTracking({
      offerRuleId:
        item?.offerRuleId != null && String(item.offerRuleId) !== ""
          ? item.offerRuleId
          : ctx.offerRuleId,
      product,
      popup: ctx.popup,
    });

    const freshProperties = buildStorefrontCartLineProperties({
      shop,
      popup: ctx.popup,
      product,
      variant: { id: item?.id },
      quantity: item?.quantity,
      slotNo,
      cartDrawerUpsell: ctx.cartDrawerUpsell === true,
      offerRuleId: resolvedOfferRuleId,
    });

    if (!freshProperties) return item;

    const {
      offerRuleId: _dropOfferRuleId,
      ...cartSafeItem
    } = item;

    if (lineItemHasStorefrontTracking(item)) {
      return {
        ...cartSafeItem,
        properties: {
          ...(item?.properties || {}),
          [CHECKOUT_TRACKING_KEYS.OFFER_RULE_ID]:
            freshProperties[CHECKOUT_TRACKING_KEYS.OFFER_RULE_ID],
          [CHECKOUT_TRACKING_KEYS.PIXEL]:
            freshProperties[CHECKOUT_TRACKING_KEYS.PIXEL],
          ...(ctx.cartDrawerUpsell === true &&
          freshProperties[CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE]
            ? {
                [CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE]:
                  freshProperties[CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE],
              }
            : {}),
        },
      };
    }

    return {
      ...cartSafeItem,
      properties: {
        ...(item?.properties || {}),
        ...freshProperties,
      },
    };
  });
}

/**
 * @param {Array<{ merchandise?: { id?: string }; attributes?: Array<{ key?: string; value?: string }> }>} cartLines
 * @param {string} popupId
 * @param {string} variantGid
 * @returns {typeof cartLines[number] | undefined}
 */
export function findCartLineByTracking(cartLines, popupId, variantGid) {
  if (!Array.isArray(cartLines) || !popupId || !variantGid) return undefined;

  return cartLines.find((line) => {
    if (line?.merchandise?.id !== variantGid) return false;
    const linePopupId = getLineAttributeValue(
      line.attributes,
      CHECKOUT_TRACKING_KEYS.POPUP_ID
    );
    return linePopupId === popupId;
  });
}

/**
 * @param {Array<{ name?: string; value?: string }> | undefined} properties
 * @returns {{ popupId: string; pixel: Record<string, unknown> | null } | null}
 */
function getLineItemPropertyName(property) {
  return property?.name || property?.key || "";
}

function sumDiscountAllocations(lineItem) {
  const allocations = lineItem?.discount_allocations;
  if (!Array.isArray(allocations) || allocations.length === 0) return 0;
  return allocations.reduce((sum, entry) => {
    const amount = Number(entry?.amount);
    return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
  }, 0);
}

function getLineItemDiscountTotal(lineItem) {
  const lineDiscount = Number(lineItem?.total_discount);
  if (Number.isFinite(lineDiscount) && lineDiscount > 0) {
    return lineDiscount;
  }
  return sumDiscountAllocations(lineItem);
}

function getLineGrossTotal(lineItem) {
  const quantity = Number(lineItem?.quantity) || 1;
  const preTaxPrice = Number(lineItem?.pre_tax_price);
  if (Number.isFinite(preTaxPrice) && preTaxPrice >= 0) {
    return preTaxPrice;
  }
  const unitPrice = Number(lineItem?.price);
  if (Number.isFinite(unitPrice) && unitPrice >= 0) {
    return unitPrice * quantity;
  }
  return null;
}

/**
 * When Shopify has not yet populated line-level discount fields, derive net line
 * revenue from order-level totals (common on early orders/updated webhooks).
 *
 * Only safe for single-line orders. Multi-line orders must use per-line discounts;
 * proportional allocation of order.total_discounts would wrongly reduce undiscounted
 * upsell lines (e.g. order-status add-on) when another line has a popup discount.
 *
 * @param {Record<string, unknown>} lineItem
 * @param {Record<string, unknown>} order
 * @param {number | null} grossLineTotal
 */
function resolveRevenueFromOrderLevel(lineItem, order, grossLineTotal) {
  if (!order || grossLineTotal == null || grossLineTotal <= 0) return null;

  const orderDiscounts = Number(order?.total_discounts);
  if (!Number.isFinite(orderDiscounts) || orderDiscounts <= 0) {
    return null;
  }

  const lineItems = Array.isArray(order?.line_items) ? order.line_items : [];
  if (lineItems.length !== 1) {
    return null;
  }

  const subtotal = Number(order?.subtotal_price);
  if (Number.isFinite(subtotal) && subtotal >= 0) {
    return Math.max(0, subtotal);
  }
  const totalPrice = Number(order?.total_price);
  if (Number.isFinite(totalPrice) && totalPrice >= 0) {
    return Math.max(0, totalPrice);
  }
  return null;
}

/**
 * Debug breakdown for upsell line revenue (order webhook line_items entry).
 *
 * @param {Record<string, unknown>} lineItem
 * @param {Record<string, unknown>} [order]
 * @returns {{
 *   revenue: number;
 *   method: string;
 *   quantity: number;
 *   unitPrice: number | null;
 *   preTaxPrice: number | null;
 *   totalDiscountField: number | null;
 *   allocatedDiscount: number;
 *   appliedDiscount: number;
 *   discountAllocations: unknown;
 *   lineItemId: unknown;
 *   variantId: unknown;
 *   title: unknown;
 * }}
 */
export function inspectUpsellLineRevenue(lineItem, order = null) {
  const quantity = Number(lineItem?.quantity) || 1;
  const unitPrice = Number(lineItem?.price);
  const preTaxPrice = Number(lineItem?.pre_tax_price);
  const totalDiscountField = Number(lineItem?.total_discount);
  const allocatedDiscount = sumDiscountAllocations(lineItem);
  const appliedDiscount = getLineItemDiscountTotal(lineItem);
  const grossLineTotal = getLineGrossTotal(lineItem);

  const preTaxFinite = Number.isFinite(preTaxPrice) && preTaxPrice >= 0;
  const grossFromPrice =
    Number.isFinite(unitPrice) && unitPrice >= 0 ? unitPrice * quantity : null;

  let method = "unknown";
  let revenue = 0;

  if (preTaxFinite) {
    method = "pre_tax_price_minus_discount";
    revenue = Math.max(0, preTaxPrice - appliedDiscount);
  } else if (grossFromPrice != null) {
    method = "price_x_qty_minus_discount";
    revenue = Math.max(0, grossFromPrice - appliedDiscount);
  }

  if (appliedDiscount <= 0 && order) {
    const lineItemCount = Array.isArray(order?.line_items)
      ? order.line_items.length
      : 0;

    // Never spread order.total_discounts across multiple lines.
    if (lineItemCount === 1) {
      const orderFallback = resolveRevenueFromOrderLevel(
        lineItem,
        order,
        grossLineTotal
      );
      if (
        orderFallback != null &&
        (revenue <= 0 || orderFallback < revenue - 0.009)
      ) {
        method = "order_level_discount_fallback";
        revenue = orderFallback;
      }
    }
  }

  return {
    revenue: normalizeLineRevenue(revenue),
    method,
    quantity,
    unitPrice: Number.isFinite(unitPrice) ? unitPrice : null,
    preTaxPrice: preTaxFinite ? preTaxPrice : null,
    totalDiscountField: Number.isFinite(totalDiscountField)
      ? totalDiscountField
      : null,
    allocatedDiscount,
    appliedDiscount,
    discountAllocations: lineItem?.discount_allocations ?? null,
    lineItemId: lineItem?.id ?? null,
    variantId: lineItem?.variant_id ?? null,
    title: lineItem?.title ?? lineItem?.name ?? null,
  };
}

/**
 * Order-level discount context from Shopify order webhooks.
 *
 * @param {Record<string, unknown>} order
 */
export function summarizeOrderDiscountContext(order) {
  const discountApplications = Array.isArray(order?.discount_applications)
    ? order.discount_applications.map((entry) => ({
        type: entry?.type ?? null,
        title: entry?.title ?? null,
        code: entry?.code ?? null,
        value: entry?.value ?? null,
        valueType: entry?.value_type ?? null,
        targetType: entry?.target_type ?? null,
        allocationMethod: entry?.allocation_method ?? null,
      }))
    : [];

  return {
    financialStatus: order?.financial_status ?? null,
    fulfillmentStatus: order?.fulfillment_status ?? null,
    totalPrice: order?.total_price ?? null,
    subtotalPrice: order?.subtotal_price ?? null,
    totalDiscounts: order?.total_discounts ?? null,
    totalTax: order?.total_tax ?? null,
    discountCodes: order?.discount_codes ?? null,
    discountApplicationCount: discountApplications.length,
    discountApplications,
  };
}

/**
 * Net paid revenue for one order line (after discounts), in shop currency units.
 * Shopify order webhooks expose unit `price` and line `total_discount` / `discount_allocations`.
 * `pre_tax_price` is the undiscounted line total (before tax), so discounts must still be subtracted.
 *
 * @param {{
 *   price?: string | number;
 *   quantity?: number;
 *   total_discount?: string | number;
 *   pre_tax_price?: string | number;
 *   discount_allocations?: Array<{ amount?: string | number }>;
 * }} lineItem
 * @returns {number}
 */
export function calculateUpsellLineRevenue(lineItem, order = null) {
  return inspectUpsellLineRevenue(lineItem, order).revenue;
}

/**
 * @param {Record<string, unknown>} order
 * @returns {{
 *   email: string;
 *   name: string;
 *   phone: string;
 *   location: Record<string, string>;
 * } | null}
 */
export function extractOrderCustomerLead(order) {
  const email = String(
    order?.email || order?.contact_email || order?.customer?.email || ""
  )
    .trim()
    .toLowerCase();

  if (!email) return null;

  const billing = order?.billing_address || {};
  const shipping = order?.shipping_address || {};
  const customer = order?.customer || {};
  const address = billing?.city ? billing : shipping;

  const name =
    address?.name ||
    [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") ||
    [billing?.first_name, billing?.last_name].filter(Boolean).join(" ") ||
    "";

  const phone =
    address?.phone ||
    customer?.phone ||
    billing?.phone ||
    shipping?.phone ||
    "";

  const location = {
    city: address?.city || "",
    region: address?.province || address?.province_code || "",
    country: address?.country || address?.country_code || "",
    postal: address?.zip || address?.postal_code || "",
  };

  return { email, name: String(name).trim(), phone: String(phone).trim(), location };
}

export function parseLineItemTrackingProperties(properties) {
  if (!Array.isArray(properties)) return null;

  const popupProp = properties.find(
    (property) =>
      getLineItemPropertyName(property) === CHECKOUT_TRACKING_KEYS.POPUP_ID
  );
  const pixelProp = properties.find(
    (property) =>
      getLineItemPropertyName(property) === CHECKOUT_TRACKING_KEYS.PIXEL
  );

  if (!popupProp?.value || !pixelProp?.value) return null;

  const pixel = parseCheckoutPixelProperty(pixelProp.value);
  if (!pixel) return null;

  return {
    popupId: String(popupProp.value),
    pixel,
  };
}

/**
 * Read a line item property from Shopify webhook array or Ajax cart object shape.
 */
export function getLineItemPropertyValue(properties, key) {
  if (!properties || !key) return undefined;

  if (Array.isArray(properties)) {
    const match = properties.find(
      (property) => getLineItemPropertyName(property) === key
    );
    const raw = match?.value;
    if (raw == null) return undefined;
    const value = String(raw);
    return value.trim() ? value : undefined;
  }

  if (typeof properties === "object") {
    const value = properties[key];
    if (value == null) return undefined;
    const str = String(value);
    return str.trim() ? str : undefined;
  }

  return undefined;
}

/**
 * Normalize Ajax cart line properties (object or array) to a plain object.
 */
export function normalizeLineItemPropertiesObject(properties) {
  if (!properties) return {};
  if (Array.isArray(properties)) {
    const out = {};
    for (const property of properties) {
      const key = getLineItemPropertyName(property);
      if (!key || property?.value == null) continue;
      out[key] = String(property.value);
    }
    return out;
  }
  if (typeof properties === "object") {
    return { ...properties };
  }
  return {};
}

/**
 * Active storefront popup id on a cart line (empty / cleared values ignored).
 */
export function getLineStorefrontPopupId(properties) {
  return (
    getLineItemPropertyValue(properties, CHECKOUT_TRACKING_KEYS.POPUP_ID) || ""
  );
}

export function getLineCartDrawerUpsellPopupId(properties) {
  return (
    getLineItemPropertyValue(
      properties,
      CHECKOUT_TRACKING_KEYS.CART_DRAWER_UPSELL_LINE
    ) || ""
  );
}

/**
 * Build /cart/change.js properties with popup tracking cleared (empty strings).
 */
export function clearStorefrontPopupTrackingOnProperties(properties) {
  const next = normalizeLineItemPropertiesObject(properties);
  next[CHECKOUT_TRACKING_KEYS.POPUP_ID] = "";
  next[CHECKOUT_TRACKING_KEYS.PIXEL] = "";
  return next;
}

/**
 * Line properties for a reward-bar product tier (discount tag + revenue pixel).
 */
export function buildRewardProductLineProperties({
  shop,
  popup,
  product,
  variantId,
  tierId,
  existingProperties,
}) {
  const merged = {
    ...(existingProperties && typeof existingProperties === "object"
      ? existingProperties
      : {}),
    [CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID]: String(tierId),
  };

  if (!resolveTrackingPopupId(popup)) {
    return merged;
  }

  const popupProps = buildStorefrontCartLineProperties({
    shop,
    popup,
    product,
    variant: { id: variantId },
    quantity: 1,
    slotNo: `reward-tier-${tierId}`,
  });

  return popupProps ? { ...merged, ...popupProps } : merged;
}

/**
 * Resolve upsell attribution for reward-bar product tier lines on orders.
 */
export function parseRewardLineItemTracking(lineItem) {
  const properties = lineItem?.properties;
  const rewardTierId = getLineItemPropertyValue(
    properties,
    CHECKOUT_TRACKING_KEYS.REWARD_TIER_ID
  );
  if (!rewardTierId) return null;

  if (Array.isArray(properties)) {
    const standard = parseLineItemTrackingProperties(properties);
    if (standard) {
      return {
        ...standard,
        source: "line_properties",
        rewardTierId,
      };
    }
  } else if (typeof properties === "object") {
    const popupId = getLineItemPropertyValue(
      properties,
      CHECKOUT_TRACKING_KEYS.POPUP_ID
    );
    const pixelRaw = getLineItemPropertyValue(
      properties,
      CHECKOUT_TRACKING_KEYS.PIXEL
    );
    const pixel = parseCheckoutPixelProperty(pixelRaw);
    if (popupId && pixel) {
      return {
        popupId,
        pixel,
        source: "line_properties",
        rewardTierId,
      };
    }
  }

  const popupId = getLineItemPropertyValue(
    properties,
    CHECKOUT_TRACKING_KEYS.POPUP_ID
  );
  const variantId = lineItem?.variant_id;
  if (!popupId || variantId == null || variantId === "") return null;

  return {
    popupId: String(popupId),
    pixel: {
      v: CHECKOUT_PIXEL_VERSION,
      store: "",
      location: STOREFRONT_TRACKING_LOCATION,
      popupId: String(popupId),
      campaignId: "",
      productId:
        lineItem?.product_id != null ? String(lineItem.product_id) : "",
      variantId: String(variantId),
      quantity: Number(lineItem?.quantity) || 1,
      slot: `reward-tier-${rewardTierId}`,
      popupType: "Custom Cart Drawer",
      rewardTierId: String(rewardTierId),
    },
    source: "reward_tier_properties",
    rewardTierId: String(rewardTierId),
  };
}

export function rewardLineHasRevenueTracking(properties) {
  return Boolean(
    getLineItemPropertyValue(properties, CHECKOUT_TRACKING_KEYS.POPUP_ID) &&
      getLineItemPropertyValue(properties, CHECKOUT_TRACKING_KEYS.PIXEL)
  );
}
